import { useMemo } from 'react';

import {
  IMarketingInspectorBlockPlan,
  IMarketingInspectorPlan,
  IMarketingInspectorTarget,
  marketingInspectorPlan,
  marketingInspectorRow,
  MARKETING_INSPECTOR_BLOCK_ROWS,
} from './marketingInspectorModel';
import { marketingMonthStart, marketingTodayWarsaw } from './marketingModel';
import {
  IMarketingBreakdown,
  IMarketingBreakdownRow,
  IMarketingCohortRow,
  IMarketingCreativeRow,
  IMarketingCreativesPage,
  IMarketingDemandRow,
  IMarketingFilter,
  IMarketingForecastRow,
  IMarketingSeriesPoint,
} from './types';
import {
  IMarketingQueryOptions,
  IMarketingQueryState,
  useMarketingBreakdown,
  useMarketingCohorts,
  useMarketingCreatives,
  useMarketingDemand,
  useMarketingForecast,
  useMarketingSeries,
} from './useMarketingReports';

/**
 * ТЗ 0088, доводка читаемости — данные панели справа. Все запросы уходят,
 * только пока панель открыта (`enabled`), с той же политикой, что у отчётов
 * раздела (`cache-and-network` доменных хуков). Число вызовов хуков
 * постоянное: неиспользуемая разбивка просто пропущена.
 */

/** Заглушка переменных пропущенного запроса (он не уходит). */
const IDLE_FILTER: IMarketingFilter = {
  dateFrom: '1970-01-01',
  dateTo: '1970-01-01',
};

/** Недельный ряд панели — всегда по неделям (решение ТЗ доводки). */
export const MARKETING_INSPECTOR_GRANULARITY = 'WEEK' as const;

export interface IMarketingInspectorBlock {
  /** Какая разбивка показана (основная или запасная). */
  plan: IMarketingInspectorBlockPlan;
  query: IMarketingQueryState<IMarketingBreakdown>;
}

export interface IMarketingInspectorData extends IMarketingInspectorOwn {
  series: IMarketingQueryState<IMarketingSeriesPoint[]>;
  blocks: IMarketingInspectorBlock[];
}

const useBlock = (
  plan: IMarketingInspectorBlockPlan | undefined,
  enabled: boolean,
) =>
  useMarketingBreakdown(
    {
      filter: plan?.filter ?? IDLE_FILTER,
      dimension: plan?.dimension ?? 'CHANNEL',
      parentKey: plan?.parentKey ?? null,
      limit: MARKETING_INSPECTOR_BLOCK_ROWS,
      offset: 0,
    },
    { enabled: enabled && Boolean(plan) },
  );

export interface IMarketingInspectorOwn {
  plan: IMarketingInspectorPlan | null;
  own: IMarketingQueryState<IMarketingBreakdown>;
  /** Строка сущности за окно; `null` — не пришла или не найдена. */
  row: IMarketingBreakdownRow | null;
  /**
   * Ответ пришёл, а строки нет, — или ключ не годится в запрос вовсе:
   * «не найдено за это окно».
   */
  notFound: boolean;
}

/**
 * Строка самой сущности — имя в шапке панели, площадка кампании для
 * «Спуститься в таблице» и плитки. Шапка и тело панели зовут этот хук с
 * одинаковыми переменными — Apollo склеивает их в один запрос.
 */
export const useMarketingInspectorOwn = (
  target: IMarketingInspectorTarget | null,
  filter: IMarketingFilter,
  { enabled = true }: IMarketingQueryOptions = {},
): IMarketingInspectorOwn => {
  const plan = useMemo(
    () => (target ? marketingInspectorPlan(target, filter) : null),
    [target, filter],
  );
  const own = useMarketingBreakdown(
    {
      filter: plan?.own.filter ?? IDLE_FILTER,
      dimension: plan?.own.dimension ?? 'CHANNEL',
      // Сужение фильтра оставляет одну строку; запас — на случай дублей.
      limit: 5,
      offset: 0,
    },
    { enabled: enabled && Boolean(plan) },
  );
  const row = target ? marketingInspectorRow(target, own.data?.rows) : null;
  // Ответ прошлой сущности или прошлого окна (`previousData`) — не «не
  // найдено» этой: пока перезапрос идёт, судить рано.
  const notFound =
    Boolean(target) &&
    (!plan || (!own.fetching && Boolean(own.data) && row === null));
  return { plan, own, row, notFound };
};

/**
 * Панель сущности (канал, кампания, группа, курс, оффер): её строка,
 * недельный ряд и две короткие разбивки. Фильтр — фильтр вкладки; сужение
 * до сущности делает модель (`marketingInspectorPlan`).
 *
 * Тело панели монтируется заново на каждую сущность (`key`), поэтому
 * разбивки не показывают строки прошлой сущности, пока едет ответ новой.
 */
export const useMarketingEntityInspector = (
  target: IMarketingInspectorTarget | null,
  filter: IMarketingFilter,
  { enabled = true }: IMarketingQueryOptions = {},
): IMarketingInspectorData => {
  const { plan, own, row, notFound } = useMarketingInspectorOwn(
    target,
    filter,
    { enabled },
  );
  const on = enabled && Boolean(plan);
  const series = useMarketingSeries(
    plan?.seriesFilter ?? null,
    MARKETING_INSPECTOR_GRANULARITY,
    { enabled: on },
  );

  const first = plan?.blocks[0];
  const second = plan?.blocks[1];
  const firstQuery = useBlock(first, on);
  const secondQuery = useBlock(second, on);
  // Запасная разбивка первой (кампания без групп → объявления) — только
  // когда основная пришла и пуста.
  const firstEmpty =
    Boolean(firstQuery.data) && firstQuery.data?.rows.length === 0;
  const fallbackQuery = useBlock(
    first?.fallback ?? undefined,
    on && firstEmpty && Boolean(first?.fallback),
  );

  const blocks: IMarketingInspectorBlock[] = [];
  if (first) {
    blocks.push(
      firstEmpty && first.fallback
        ? { plan: first.fallback, query: fallbackQuery }
        : { plan: first, query: firstQuery },
    );
  }
  if (second) blocks.push({ plan: second, query: secondQuery });

  return { plan, own, row, notFound, series, blocks };
};

export interface IMarketingCourseInspectorData {
  forecast: IMarketingQueryState<IMarketingForecastRow[]>;
  /** Прогноз этого курса; `null` — у курса нет прогноза (не продаётся). */
  forecastRow: IMarketingForecastRow | null;
  cohorts: IMarketingQueryState<IMarketingCohortRow[]>;
  demand: IMarketingQueryState<IMarketingDemandRow[]>;
  /** Спрос в поиске по курсу; `null` — нет строки. */
  demandRow: IMarketingDemandRow | null;
}

/**
 * Дополнительно для курса: прогноз ТЕКУЩЕГО месяца (от окна не зависит),
 * когорты недель входа с фильтром курса и спрос в поиске (только окно:
 * Search Console о каналах и юрлицах ничего не знает).
 */
export const useMarketingCourseInspector = (
  slug: string | null,
  filter: IMarketingFilter,
  { enabled = true }: IMarketingQueryOptions = {},
): IMarketingCourseInspectorData => {
  const on = enabled && Boolean(slug);
  const month = marketingMonthStart(marketingTodayWarsaw());
  const forecast = useMarketingForecast(month, { enabled: on });
  const cohortFilter = useMemo(
    () => (slug ? { ...filter, familySlugs: [slug] } : filter),
    [filter, slug],
  );
  const cohorts = useMarketingCohorts(cohortFilter, { enabled: on });
  const siteFilter = useMemo(
    () => ({ dateFrom: filter.dateFrom, dateTo: filter.dateTo }),
    [filter.dateFrom, filter.dateTo],
  );
  const demand = useMarketingDemand(siteFilter, { enabled: on });
  const forecastRow =
    forecast.data?.find((row) => row.familySlug === slug) ?? null;
  const demandRow = demand.data?.find((row) => row.familySlug === slug) ?? null;
  return { forecast, forecastRow, cohorts, demand, demandRow };
};

/* ── Объявление ──────────────────────────────────────────────────────── */

/** id объявления и группы ассетов — число; иное в запрос не везём. */
const AD_ID_RE = /^\d+$/;

export interface IMarketingAdInspectorData {
  query: IMarketingQueryState<IMarketingCreativesPage>;
  /** Строка объявления за окно; `null` — не пришла или не найдена. */
  row: IMarketingCreativeRow | null;
  /** Ответ пришёл, а строки нет, — или id не годится: «не найдено». */
  notFound: boolean;
}

/**
 * Панель объявления (или группы ассетов PMax): ровно оно запросом `adIds` —
 * мимо измерений фильтра, формата и «только выгорающих» (окно, модель и
 * юрлицо действуют), с рядом CTR / CPM (по дням, пока окно ≤ 92 дней, иначе
 * по неделям). Шапка и тело панели зовут хук с одинаковыми переменными —
 * Apollo склеивает их в один запрос.
 */
export const useMarketingAdInspector = (
  adId: string | null,
  filter: IMarketingFilter,
  { enabled = true }: IMarketingQueryOptions = {},
): IMarketingAdInspectorData => {
  const valid = Boolean(adId) && AD_ID_RE.test(adId ?? '');
  const query = useMarketingCreatives(
    {
      filter: valid ? filter : IDLE_FILTER,
      fatigueOnly: false,
      format: null,
      orderBy: null,
      limit: 1,
      offset: 0,
      withSeries: true,
      adIds: valid && adId ? [adId] : [],
    },
    { enabled: enabled && valid },
  );
  // Строка другого объявления (прошлый ответ) — не «прошлые данные» этого.
  const row = query.data?.rows.find((item) => item.id === adId) ?? null;
  const notFound =
    Boolean(adId) &&
    (!valid || (!query.fetching && Boolean(query.data) && row === null));
  return { query, row, notFound };
};
